<template>
  <div>
    <DeleteModal modal-id="modal-drugs-delete" @deleteToggle="deleteItem" />

    <b-modal
      id="modal-not-found"
      ok-variant="warning"
      :title="$t('common.Confirm Not Found')"
      @ok="confirmNotFound"
    >
      <p>
        {{ $t("common.Are you sure you want to mark this drug as not found?") }}
      </p>
    </b-modal>

    <b-modal
      id="modal-drugs-edit"
      :no-close-on-backdrop="true"
      ok-only
      centered
      size="lg"
      :title="$t('pharmacy.Edit Patient Card Drug')"
      @ok="submitItem()"
    >
      <b-card-text>
        <b-row>
          <b-col md="">
            <AutoComplete
              v-model="EditForm.drug_id"
              label="Drug"
              placeholder="Select Drug"
              param-query="search"
              :serializer="(val) => val.id"
              endpoint="drug"
              text-title="name"
            />
          </b-col>
          <b-col md="">
            <b-form-group :label="$t('common.price')" label-for="Price">
              <b-form-input
                id="Price"
                v-model="EditForm.price"
                type="number"
                :placeholder="$t('common.price')"
              />
            </b-form-group>
          </b-col>
        </b-row>
        <b-row>
          <b-col md="">
            <b-form-group :label="$t('pharmacy.Dose')" label-for="Dose">
              <b-form-input
                id="Dose"
                v-model="EditForm.dose"
                type="number"
                :placeholder="$t('pharmacy.Dose')"
              />
            </b-form-group>
          </b-col>
          <b-col md="">
            <b-form-group :label="$t('pharmacy.status')">
              <v-select
                v-model="EditForm.status"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="statusList"
                label="text"
                :reduce="(status) => status.value"
                :placeholder="$t('pharmacy.status')"
              />
            </b-form-group>
          </b-col>
        </b-row>
        <b-row>
          <b-col md="">
            <b-form-group :label="$t('pharmacy.Quantity')" label-for="Quantity">
              <b-form-input
                id="Quantity"
                v-model="EditForm.quantity"
                type="number"
                :placeholder="$t('pharmacy.Quantity')"
              />
            </b-form-group>
          </b-col>
        </b-row>
      </b-card-text>
    </b-modal>

    <!-- External Drug Dispense Modal -->
    <b-modal
      id="modal-external-dispense"
      :no-close-on-backdrop="true"
      ok-only
      centered
      size="md"
      :title="$t('pharmacy.dispense-external')"
      @ok="submitExternalDispense()"
    >
      <b-card-text>
        <b-row>
          <b-col class="mt-1" lg="12">
            <h5>
              {{ $t("common.patient-name") }}:
              <b-badge pill variant="light-success">
                {{ externalDispenseForm.patientName }}
              </b-badge>
            </h5>
          </b-col>
          <b-col class="mt-1" lg="12">
            <h5>
              {{ $t("common.name") }}:
              <b-badge pill variant="light-info">
                {{ externalDispenseForm.drugName }}
              </b-badge>
            </h5>
          </b-col>
          <b-col class="mt-1" lg="12">
            <h6>
              {{ $t("pharmacy.Quantity") }} {{ $t("common.total_requested") }}:
              <b-badge pill variant="light-warning">
                {{ externalDispenseForm.totalQuantity }}
              </b-badge>
            </h6>
          </b-col>
          <b-col class="mt-1" lg="12">
            <b-form-group
              :label="$t('pharmacy.Quantity') + ' ' + $t('common.dispense')"
              label-for="DispenseQuantity"
            >
              <b-form-input
                id="DispenseQuantity"
                v-model="externalDispenseForm.dispenseQuantity"
                type="number"
                :placeholder="$t('pharmacy.Quantity')"
                :max="externalDispenseForm.totalQuantity"
                min="1"
                required
              />
              <small class="text-muted">
                {{ $t("common.maxValue") }}:
                {{ externalDispenseForm.totalQuantity }}
              </small>
            </b-form-group>
          </b-col>
        </b-row>
      </b-card-text>
    </b-modal>

    <!-- Dispense Drug Modal -->
    <b-modal
      id="modal-dispense-drug"
      :no-close-on-backdrop="true"
      ok-only
      centered
      size="xl"
      :title="$t('pharmacy.dispense-drug')"
      @ok="submitDispense()"
    >
      <!-- Step 1: Patient Info & Date Selection -->
      <div v-if="!dispenseForm.isBarCodeStep">
        <b-card class="shadow-sm border-0">
          <template #header>
            <div class="d-flex align-items-center">
              <feather-icon
                icon="UserIcon"
                size="20"
                class="mr-2 text-primary"
              />
              <h5 class="mb-0 text-primary font-weight-bold">
                {{ $t("warehouse.order-process") }}
              </h5>
            </div>
          </template>

          <b-card-text class="p-3">
            <!-- Compact Patient and Item Info -->
            <div class="mb-3">
              <div
                class="d-flex justify-content-between align-items-center mb-2"
              >
                <div class="d-flex align-items-center">
                  <feather-icon
                    icon="UserIcon"
                    size="14"
                    class="mr-1 text-success"
                  />
                  <small class="text-muted"
                    >{{ $t("common.patient-name") }}:</small
                  >
                </div>
                <span class="font-weight-bold text-dark">{{
                  dispenseForm.patientName
                }}</span>
              </div>
              <div
                class="d-flex justify-content-between align-items-center mb-2"
              >
                <div class="d-flex align-items-center">
                  <feather-icon
                    icon="PackageIcon"
                    size="14"
                    class="mr-1 text-info"
                  />
                  <small class="text-muted">{{ $t("common.name") }}:</small>
                </div>
                <span class="font-weight-bold text-dark">{{
                  dispenseForm.itemName
                }}</span>
              </div>
            </div>

            <!-- Compact Date Selection -->
            <b-row class="mb-3">
              <b-col>
                <b-form-group class="mb-2">
                  <label class="form-label small">
                    <feather-icon icon="CalendarIcon" size="14" class="mr-1" />
                    {{ $t("common.date") }}
                  </label>
                  <datepicker
                    v-model="dispenseForm.date"
                    :calendar-button="true"
                    :clear-button="true"
                    :bootstrap-styling="true"
                    class="datebtn2"
                    placeholder="YYYY-MM-DD H:i"
                  />
                </b-form-group>
              </b-col>
            </b-row>

            <!-- Compact Scan Button -->
            <div v-if="!dispenseForm.showTables" class="text-center">
              <b-button
                variant="primary"
                class="px-4 py-2"
                @click="dispenseForm.isBarCodeStep = true"
              >
                <feather-icon icon="CameraIcon" size="16" class="mr-2" />
                {{ $t("warehouse.scan-barcode") }}
              </b-button>
            </div>
          </b-card-text>
        </b-card>
      </div>

      <!-- Step 2: Barcode Scanner -->
      <div v-if="dispenseForm.isBarCodeStep">
        <b-card class="shadow-sm border-0">
          <template #header>
            <div
              class="d-flex align-items-center justify-content-between w-100"
            >
              <div class="d-flex align-items-center">
                <feather-icon
                  icon="CameraIcon"
                  size="20"
                  class="mr-2 text-primary"
                />
                <h5 class="mb-0 text-primary font-weight-bold">
                  {{ $t("warehouse.scan-barcode") }}
                </h5>
              </div>
              <div class="ml-auto">
                <b-button
                  variant="outline-secondary"
                  size="sm"
                  @click="dispenseForm.isBarCodeStep = false"
                >
                  <feather-icon icon="ArrowLeftIcon" size="16" class="mr-1" />
                  {{ $t("common.back") }}
                </b-button>
              </div>
            </div>
          </template>

          <ScanBarcode @barcode="addToItemsList($event)" />
        </b-card>
      </div>

      <!-- Step 3: Available Items Selection -->
      <div v-if="dispenseForm.showTables">
        <b-card class="mb-2">
          <template #header>
            <div class="d-flex align-items-center justify-content-between py-1">
              <div class="d-flex align-items-center">
                <feather-icon
                  icon="PackageIcon"
                  size="16"
                  class="mr-2 text-success"
                />
                <h6 class="mb-0 text-success">
                  {{ $t("warehouse.item") }}
                </h6>
              </div>
              <b-badge variant="light-success" class="px-2 py-1 small">
                {{ dispenseForm.items.length }}
              </b-badge>
            </div>
          </template>

          <b-table
            v-if="!dispenseLoading"
            :items="dispenseForm.items"
            responsive
            :fields="dispenseItemsColumns"
            primary-key="id"
            show-empty
            empty-text="No matching results found"
            class="position-relative modern-table"
            striped
            hover
          >
            <template #cell(expiry_date)="data">
              <b-badge
                pill
                :variant="getBadgeVariant(data.item.expiry_date)"
                class="font-weight-bold"
              >
                {{ data.item.expiry_date }}
              </b-badge>
            </template>
            <template #cell(changeable-quantity)="data">
              <b-form-input
                v-model="dispenseForm.quantity"
                type="number"
                :placeholder="$t('warehouse.quantity')"
                class="form-control-sm"
                min="1"
              />
            </template>
            <template #cell(unit_price)="data">
              <div class="d-flex align-items-center">
                <feather-icon
                  icon="DollarSignIcon"
                  size="14"
                  class="mr-1 text-success"
                />
                <span class="font-weight-bold text-success">{{
                  data.item.unit_price
                }}</span>
              </div>
            </template>
            <template #cell(quantity)="data">
              <b-badge variant="light-info" class="px-2 py-1">
                {{ data.item.quantity }}
              </b-badge>
            </template>
            <template #cell(actions)="data">
              <b-button
                pill
                size="sm"
                variant="success"
                class="shadow-sm"
                @click="addToStockOuts(data.item)"
              >
                <feather-icon icon="PlusIcon" size="14" class="" />
                <!-- {{ $t('common.add') }} -->
              </b-button>
            </template>
          </b-table>

          <template v-if="dispenseLoading">
            <div class="text-center py-4">
              <b-spinner variant="primary" class="mb-2" />
              <p class="text-muted mb-0">{{ $t("common.loading") }}...</p>
            </div>
          </template>
        </b-card>

        <!-- Step 4: Selected Items for Dispensing -->
        <b-card
          class="shadow-sm border-0"
          :class="{ 'border-success': dispenseForm.stockouts.length > 0 }"
        >
          <template #header>
            <div class="d-flex align-items-center justify-content-between">
              <div class="d-flex align-items-center">
                <feather-icon
                  icon="ShoppingCartIcon"
                  size="20"
                  class="mr-2 text-primary"
                />
                <h5 class="mb-0 text-primary font-weight-bold">
                  {{ $t("warehouse.stockout-items") }}
                </h5>
              </div>
              <div class="d-flex align-items-center">
                <b-badge
                  :variant="
                    dispenseForm.stockouts.length > 0 ? 'success' : 'light'
                  "
                  class="ml-2 mr-2"
                >
                  {{ dispenseForm.stockouts.length }}
                  {{ $t("common.selected") }}
                </b-badge>
                <b-button
                  v-if="dispenseForm.stockouts.length > 0"
                  variant="outline-danger"
                  size="sm"
                  @click="dispenseForm.stockouts = []"
                >
                  <feather-icon icon="TrashIcon" size="14" class="mr-1" />
                  {{ $t("common.clear-all") }}
                </b-button>
              </div>
            </div>
          </template>

          <div
            v-if="dispenseForm.stockouts.length === 0"
            class="text-center py-4"
          >
            <feather-icon
              icon="ShoppingCartIcon"
              size="48"
              class="text-muted mb-3"
            />
            <h6 class="text-muted">
              {{ $t("common.no-items-selected") }}
            </h6>
            <p class="text-muted small mb-0">
              {{ $t("common.scan-items-to-add") }}
            </p>
          </div>

          <b-table
            v-else
            :items="dispenseForm.stockouts"
            responsive
            :fields="dispenseTableColumns"
            show-empty
            empty-text="No items selected"
            class="position-relative modern-table"
            striped
            hover
          >
            <template #cell(expiry_date)="data">
              <b-badge
                pill
                :variant="getBadgeVariant(data.item.expiry_date)"
                class="font-weight-bold"
              >
                {{ data.item.expiry_date }}
              </b-badge>
            </template>
            <template #cell(quantity)="data">
              <b-badge
                pill
                variant="success"
                class="px-2 py-1 font-weight-bold"
              >
                {{ data.item.quantity }}
              </b-badge>
            </template>
            <template #cell(unit_price)="data">
              <div class="d-flex align-items-center">
                <feather-icon
                  icon="DollarSignIcon"
                  size="14"
                  class="mr-1 text-success"
                />
                <span class="font-weight-bold text-success">{{
                  data.item.unit_price
                }}</span>
              </div>
            </template>
            <template #cell(actions)="data">
              <b-button
                size="sm"
                variant="outline-danger"
                class="shadow-sm"
                @click="removeDispenseItem(data.item)"
              >
                <feather-icon icon="XIcon" size="14" />
              </b-button>
            </template>
          </b-table>
        </b-card>
      </div>
    </b-modal>
    <b-card>
      <div>
        <!-- Data Table  -->
        <!-- <div class="d-flex justify-content-between mt-4 mb-2">
          <h2>{{ $t("pharmacy.Patient Cards Drugs") }}</h2>
          <b-button
            v-if="!isHistory"
            variant="primary"
            @click="
              $router.push({
                name: 'add-patient-drug',
                params: { patient_card_id: patientCardId },
              })
            "
          >
            {{ $t("pharmacy.Add Drugs") }}
          </b-button>
        </div> -->

        <b-table
          v-if="!loading"
          :items="data"
          responsive
          :fields="tableColumns"
          primary-key="id"
          show-empty
          empty-text="No matching results found"
          class="position-relative"
        >
          <template #cell(drug_name)="data">
            {{
              data.item.item && data.item.item.name
                ? data.item.item.name
                : data.item.item_name
            }}
          </template>
          <template #cell(quantity)="data">
            <div class="d-flex flex-column">
              <b-badge variant="success" class="mb-1">
                {{ data.item.dispensed_quantity || 0 }}
                {{ $t("common.dispensed") }}
              </b-badge>
              <b-badge variant="info">
                {{ data.item.quantity || 0 }} {{ $t("common.total_requested") }}
              </b-badge>
            </div>
          </template>
          <template #cell(status)="data">
            <div>
              <b-badge
                :variant="
                  data.item.status === 0
                    ? 'warning'
                    : data.item.status === 1
                    ? 'success'
                    : data.item.status === 2
                    ? 'danger'
                    : data.item.status === 3
                    ? 'info'
                    : data.item.status === 4
                    ? 'primary'
                    : 'secondary'
                "
              >
                {{
                  data.item.status === 0
                    ? $t("pharmacy.status-pending")
                    : data.item.status === 1
                    ? $t("pharmacy.status-dispensed")
                    : data.item.status === 2
                    ? $t("pharmacy.status-not-found")
                    : data.item.status === 3
                    ? $t("pharmacy.status-external")
                    : data.item.status === 4 &&
                      $t("pharmacy.status-partially-dispensed")
                }}
              </b-badge>
            </div>
          </template>
          <template v-if="!isHistory" #cell(actions)="data">
            <div class="d-flex text-nowrap">
              <div v-if="$route.name !== 'active-patient-card' && showDispense">
                <!-- Direct dispense for external items (status 3) -->
                <b-button
                  v-if="data.item.item_name"
                  size="sm"
                  pill
                  variant="outline-success"
                  class="mb-75 mx-1"
                  @click="directDispense(data.item)"
                  >{{ $t("pharmacy.dispense-external") }}</b-button
                >
                <!-- Normal dispense process for other statuses -->
                <b-button
                  v-else
                  size="sm"
                  pill
                  variant="outline-primary"
                  class="mb-75 mx-1"
                  @click="dispanceDrug(data.item)"
                  >{{ $t("pharmacy.dispense-drug") }}</b-button
                >
                <b-button
                  size="sm"
                  pill
                  variant="outline-warning"
                  class="mb-75 mx-1"
                  @click="notFound(data.item)"
                  >{{ $t("common.not-found") }}</b-button
                >
              </div>
              <ActionsButtons
                v-if="!isHistory"
                delete-perm="delete-patient"
                edit-perm=" "
                @editToggle="show(data.item)"
                @deleteToggle="deleteModel(data.item)"
              />
            </div>
          </template>
        </b-table>
        <template v-if="loading">
          <div>
            <div class="d-flex justify-content-center mb-1">
              <b-spinner label="Loading..." />
            </div>
          </div>
        </template>
        <div class="mx-2 mb-2">
          <b-row>
            <b-col
              cols="12"
              sm="6"
              class="d-flex align-items-center justify-content-center justify-content-sm-start"
            >
              <span class="text-muted"
                >Showing 1 to 10 Patient Cards Drugs</span
              >
            </b-col>
            <!-- Pagination -->
            <b-col
              cols="12"
              sm="6"
              class="d-flex align-items-center justify-content-center justify-content-sm-end"
              @click="getPatientDrugsList()"
            >
              <b-pagination
                v-model="page"
                :total-rows="this.dataCount"
                :per-page="take"
                first-number
                last-number
                class="mb-0 mt-1 mt-sm-0"
                prev-class="prev-item"
                next-class="next-item"
              >
                <template #prev-text>
                  <feather-icon icon="ChevronLeftIcon" size="18" />
                </template>
              </b-pagination>
            </b-col>
          </b-row>
        </div>
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BFormGroup,
  BButton,
  BCol,
  BRow,
  BFormInput,
  BTable,
  BDropdown,
  BDropdownItem,
  BPagination,
  BBadge,
  BSpinner,
  BCardText,
} from "bootstrap-vue";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import vSelect from "vue-select";
import ActionsButtons from "@/components/common/ActionsButtons.vue";
import DeleteModal from "@/components/modals/DeleteModal.vue";
import Datepicker from "vuejs-datepicker";

const AutoComplete = () => import("@/components/common/AutoComplete.vue");
const ScanBarcode = () => import("@/views/warehouse/ScanBarcode.vue");

export default {
  components: {
    DeleteModal,
    ActionsButtons,
    BCard,
    BBadge,
    BFormGroup,
    BButton,
    BCol,
    BRow,
    BFormInput,
    BTable,
    BDropdown,
    BDropdownItem,
    BPagination,
    BSpinner,
    BCardText,
    AutoComplete,
    vSelect,
    Datepicker,
    ScanBarcode,
  },
  props: ["patientCardId", "isHistory", "showDispense", "hideAddButton"],

  data() {
    return {
      take: 10,
      loading: true,
      isNotValid: false,
      patientCard: "",
      selectedDrug: null,
      statusList: [
        {
          text: this.$t("pharmacy.status-not-found"),
          value: "2",
        },
        {
          text: this.$t("pharmacy.status-external"),
          value: "3",
        },
        {
          text: this.$t("pharmacy.status-partially-dispensed"),
          value: "4",
        },
      ],
      dataCount: 0,
      data: [],
      EditForm: {
        drug_id: "",
        patient_card_id: "",
        quantity: "",
        dose: "",
        price: "",
        status: "",
        _method: "PUT",
      },
      errorForm: {
        drug_id: "",
        patient_card_id: "",
        quantity: "",
        dose: "",
        price: "",
        status: "",
      },
      params: {
        form: {},
        path: "",
      },
      tableColumns: [
        { key: "drug_name", label: this.$t("common.name"), sortable: true },

        {
          key: "quantity",
          sortable: true,
          label: this.$t("pharmacy.Quantity"),
        },
        { key: "dose", label: this.$t("pharmacy.Dose"), sortable: true },
        { key: "status", label: this.$t("pharmacy.status"), sortable: true },
        { key: "Actions", label: this.$t("common.actions") },
      ],
      currentPage: 1,
      takeOptions: [10, 25, 50, 100],
      searchQuery: "",
      sortBy: "id",
      isSortDirDesc: true,
      page: 1,
      // Dispense Drug Modal Data
      dispenseLoading: false,
      currentDispenseItem: null,
      dispenseForm: {
        isBarCodeStep: false,
        showTables: false,
        patientName: "",
        itemName: "",
        date: this.formatExpDate(Date.now()),
        quantity: 0,
        items: [],
        stockouts: [],
      },
      dispenseItemsColumns: [
        { key: "item.name", label: this.$t("common.name"), sortable: true },
        {
          key: "category.name",
          label: this.$t("warehouse.one-category"),
          sortable: true,
        },
        {
          key: "warehouse.name",
          label: this.$t("warehouse.one-warehouse"),
          sortable: true,
        },
        {
          key: "batch_no",
          label: this.$t("warehouse.batch-no"),
          sortable: true,
        },
        {
          key: "expiry_date",
          label: this.$t("warehouse.exp-date"),
          sortable: true,
        },
        {
          key: "quantity",
          label: this.$t("warehouse.quantity"),
          sortable: true,
        },
        {
          key: "changeable-quantity",
          label: this.$t("warehouse.change-quantity"),
          sortable: true,
        },
        {
          key: "unit_price",
          label: this.$t("warehouse.price"),
          sortable: true,
        },
        { key: "actions", label: this.$t("common.actions") },
      ],
      dispenseTableColumns: [
        { key: "item_id", label: this.$t("common.name"), sortable: true },
        {
          key: "warehouse_id",
          label: this.$t("warehouse.one-warehouse"),
          sortable: true,
        },
        {
          key: "batch_no",
          label: this.$t("warehouse.batch-no"),
          sortable: true,
        },
        {
          key: "quantity",
          label: this.$t("warehouse.quantity"),
          sortable: true,
        },
        {
          key: "unit_price",
          label: this.$t("warehouse.price"),
          sortable: true,
        },
        {
          key: "expiry_date",
          label: this.$t("warehouse.exp-date"),
          sortable: true,
        },
        { key: "actions", label: this.$t("common.actions") },
      ],
      // External Dispense Modal Data
      currentExternalItem: null,
      externalDispenseForm: {
        patientName: "",
        drugName: "",
        totalQuantity: 0,
        dispenseQuantity: 1,
      },
    };
  },
  watch: {
    patientCardId: {
      handler(newVal, oldVal) {
        // Only fetch if we have a valid ID and it actually changed from invalid to valid
        if (newVal && newVal !== "-" && newVal !== "" && newVal !== oldVal) {
          this.getPatientDrugsList();
        }
      },
      immediate: false,
    },
  },
  mounted() {
    // Only fetch if we have a valid patientCardId on mount
    if (
      this.patientCardId &&
      this.patientCardId !== "-" &&
      this.patientCardId !== ""
    ) {
      this.getPatientDrugsList();
    }
  },

  methods: {
    async dispanceDrug(item) {
      this.currentDispenseItem = item;
      this.dispenseForm.quantity = item.quantity;

      // Reset modal state
      this.dispenseForm.isBarCodeStep = false;
      this.dispenseForm.showTables = false;
      this.dispenseForm.items = [];
      this.dispenseForm.stockouts = [];

      // Get item and patient data
      await this.getDispenseItemData(item.item_id);
      await this.getDispensePatientData(item.patient_card_id);

      this.$bvModal.show("modal-dispense-drug");
    },

    async directDispense(item) {
      this.currentExternalItem = item;

      // Set form data
      this.externalDispenseForm.drugName =
        item.item && item.item.name ? item.item.name : item.item_name;
      this.externalDispenseForm.totalQuantity = item.quantity;
      this.externalDispenseForm.dispenseQuantity = Math.min(1, item.quantity); // Default to 1 or max available

      // Get patient data
      await this.getExternalDispensePatientData(item.patient_card_id);

      // Show modal
      this.$bvModal.show("modal-external-dispense");
    },

    async getExternalDispensePatientData(patientCardId) {
      try {
        const url = `/patientCard/${patientCardId}`;
        await this.getList(url);
        this.externalDispenseForm.patientName = this.all_data.patient
          ? this.all_data.patient.name
          : "Unknown Patient";
      } catch (error) {
        console.error("Error fetching patient data:", error);
        this.externalDispenseForm.patientName = "Unknown Patient";
      }
    },

    async submitExternalDispense() {
      // Validation
      if (
        !this.externalDispenseForm.dispenseQuantity ||
        this.externalDispenseForm.dispenseQuantity <= 0
      ) {
        this.$toast({
          component: ToastificationContent,
          position: "top-right",
          props: {
            title: "Validation Error",
            variant: "danger",
            text: "Please enter a valid quantity to dispense",
          },
        });
        return;
      }

      if (
        this.externalDispenseForm.dispenseQuantity >
        this.externalDispenseForm.totalQuantity
      ) {
        this.$toast({
          component: ToastificationContent,
          position: "top-right",
          props: {
            title: "Validation Error",
            variant: "danger",
            text: "Dispense quantity cannot exceed total requested quantity",
          },
        });
        return;
      }

      try {
        const params = {
          form: {
            quantity: this.externalDispenseForm.dispenseQuantity,
            status: 3, // External status
          },
          path: `/patientCardDrug/dispense/${this.currentExternalItem.id}`,
        };

        await this.$store.dispatch("create", params);

        this.$toast({
          component: ToastificationContent,
          position: "top-right",
          props: {
            title: "Success",
            variant: "success",
            text: `External drug dispensed successfully (${this.externalDispenseForm.dispenseQuantity} units)`,
          },
        });

        this.getPatientDrugsList();
        this.$bvModal.hide("modal-external-dispense");
      } catch (error) {
        if (error.response && error.response.status === 400) {
          for (const errorItem in error.response.data) {
            this.$toast({
              component: ToastificationContent,
              position: "top-right",
              props: {
                title: "Validation Error",
                variant: "danger",
                text: error.response.data[errorItem],
              },
            });
          }
        } else {
          this.$toast({
            component: ToastificationContent,
            position: "top-right",
            props: {
              title: "Error",
              variant: "danger",
              text: "Failed to dispense external drug",
            },
          });
        }
      }
    },

    show(item) {
      this.EditForm.id = item.id;
      this.EditForm.drug_id = item.drug_id;
      this.EditForm.patient_card_id = this.patientCardId;
      this.EditForm.quantity = item.quantity;
      this.EditForm.dose = item.dose;
      this.EditForm.price = item.price;
      this.EditForm.status = item.status.toString();

      this.EditForm._method = "PUT";

      this.$bvModal.show("modal-drugs-edit");
    },

    async getPatientDrugsList() {
      if (
        !this.patientCardId ||
        this.patientCardId === "-" ||
        this.patientCardId === ""
      ) {
        this.loading = false;
        this.data = [];
        this.dataCount = 0;
        return;
      }
      this.loading = true;
      // check if there are any null value in the obj we sending

      const url = `/patientCardDrug?filter[patient_card_id]=${this.patientCardId}&page=${this.page}&take=${this.take}&sort=-id&include=item`;

      await this.getList(url);
      this.dataCount = this.all_data.total;
      this.data = this.all_data.data;
      this.loading = false;
    },

    deleteModel(item) {
      this.deleteForm = item;
      this.$bvModal.show("modal-drugs-delete");
    },
    deleteItem() {
      this.$store
        .dispatch("delete", `/patientCardDrug/${this.deleteForm.id}`)
        .then((response) => {
          this.getPatientDrugsList();

          this.$toast({
            component: ToastificationContent,
            position: "top-right",
            props: {
              title: "Delete Success",
              icon: "success",
              variant: "info",
              text: `deleted ${this.deleteForm.first_name} successfully`,
            },
          });
        })
        .catch((error) => {
          this.$toast({
            component: ToastificationContent,
            position: "top-right",
            props: {
              title: "Error",
              variant: "danger",
              text: `Error deleting ${this.deleteForm.first_name}`,
            },
          });
        });
    },
    submitItem() {
      for (const item in this.errorForm) {
        this.errorForm[item] = "";
        if (this.EditForm[item] == "" || this.EditForm[item] == null)
          delete this.EditForm[item];
      }

      (this.params.form = this.EditForm),
        (this.params.path = `/patientCardDrug/${this.EditForm.id}`),
        this.$store
          .dispatch("update", this.params)
          .then((response) => {
            this.getPatientDrugsList(this.patientCard);
          })
          .catch((error) => {
            if (error.response.status === 400) {
              for (const item in error.response.data) {
                this.$toast({
                  component: ToastificationContent,
                  position: "top-right",
                  props: {
                    title: "validation error",
                    variant: "danger",
                    text: error.response.data[item][0],
                  },
                });
              }
            }
          });
    },
    notFound(item) {
      this.selectedDrug = item;
      this.$bvModal.show("modal-not-found");
    },

    async confirmNotFound() {
      try {
        const request = await this.$store.dispatch("update", {
          path: `/patientCardDrug/dispense/${this.selectedDrug.id}`,
          form: {
            status: 2,
            quantity: this.selectedDrug.quantity,
          },
        });

        this.$toast({
          component: ToastificationContent,
          position: "top-right",
          props: {
            title: this.$t("pharmacy.Drug Marked as Not Found"),
            variant: "success",
            text: request?.data?.message,
          },
        });

        this.getPatientDrugsList();
      } catch (error) {
        console.log(error);
        this.$toast({
          component: ToastificationContent,
          position: "top-right",
          props: {
            title: "Error",
            variant: "danger",
            text: error.response?.data?.message,
          },
        });
      }
    },

    // Dispense Drug Modal Methods
    async getDispenseItemData(itemId) {
      this.dispenseLoading = true;
      try {
        const url = `/item/${itemId}`;
        await this.getList(url);
        this.dispenseForm.itemName = this.all_data.name || "Unknown Item";
      } catch (error) {
        console.error("Error fetching item data:", error);
      }
      this.dispenseLoading = false;
    },

    async getDispensePatientData(patientCardId) {
      this.dispenseLoading = true;
      try {
        const url = `/patientCard/${patientCardId}`;
        await this.getList(url);
        this.dispenseForm.patientName = this.all_data.patient
          ? this.all_data.patient.name
          : "Unknown Patient";
      } catch (error) {
        console.error("Error fetching patient data:", error);
      }
      this.dispenseLoading = false;
    },

    async addToItemsList(barcode) {
      this.dispenseLoading = true;
      try {
        const url = `/balance?filter[barcode]=${barcode}&filter[quantity-ge]=${this.dispenseForm.quantity}&sort=expiry_date&include=category,item,warehouse`;
        await this.getList(url);

        this.dispenseForm.items = this.all_data.data || [];

        if (this.dispenseForm.items.length === 0) {
          this.$toast({
            component: ToastificationContent,
            position: "top-right",
            props: {
              title: this.$t("common.item-not-found"),
              variant: "danger",
              timeout: 10000,
            },
          });
        } else {
          this.dispenseForm.isBarCodeStep = false;
          this.dispenseForm.showTables = true;
        }
      } catch (error) {
        console.error("Error fetching items:", error);
        this.$toast({
          component: ToastificationContent,
          position: "top-right",
          props: {
            title: "Error",
            variant: "danger",
            text: "Failed to fetch items",
          },
        });
      }
      this.dispenseLoading = false;
    },

    getBadgeVariant(date) {
      const currentDate = new Date();
      const currentMonth = currentDate.getMonth();
      const currentYear = currentDate.getFullYear();

      const itemDate = new Date(date);
      const itemMonth = itemDate.getMonth();
      const itemYear = itemDate.getFullYear();

      return itemMonth === currentMonth && itemYear === currentYear
        ? "danger"
        : "success";
    },

    addToStockOuts(item) {
      const stockoutItem = {
        ...item,
        expiry_date: this.formatExpDate(item.expiry_date),
        item_id: item.item ? item.item.name : "Unknown",
        warehouse_id: item.warehouse ? item.warehouse.name : "Unknown",
        batch_no: item.batch_no,
        balance_id: item.id,
        barcode: item.barcode,
        quantity: this.dispenseForm.quantity,
      };
      this.dispenseForm.stockouts.push(stockoutItem);
    },

    removeDispenseItem(item) {
      this.dispenseForm.stockouts = this.dispenseForm.stockouts.filter(
        (stockout) => stockout !== item
      );
    },

    formatExpDate(expDate) {
      const date = new Date(expDate);
      const year = date.getFullYear();
      let month = `${date.getMonth() + 1}`;
      let day = `${date.getDate()}`;

      if (month.length < 2) month = `0${month}`;
      if (day.length < 2) day = `0${day}`;

      return `${year}-${month}-${day}`;
    },

    async submitDispense() {
      if (this.dispenseForm.stockouts.length === 0) {
        this.$toast({
          component: ToastificationContent,
          position: "top-right",
          props: {
            title: "Error",
            variant: "danger",
            text: "Please add at least one item to dispense",
          },
        });
        return;
      }

      try {
        const params = {
          form: {
            quantity: this.dispenseForm.stockouts[0].quantity,
          },
          path: `/patientCardDrug/dispense/${this.currentDispenseItem.id}`,
        };

        await this.$store.dispatch("create", params);

        this.$toast({
          component: ToastificationContent,
          position: "top-right",
          props: {
            title: "Success",
            variant: "success",
            text: "Drug dispensed successfully",
          },
        });

        this.getPatientDrugsList();
        this.$bvModal.hide("modal-dispense-drug");
      } catch (error) {
        if (error.response && error.response.status === 400) {
          this.dispenseForm.stockouts = [];
          for (const item in error.response.data) {
            this.$toast({
              component: ToastificationContent,
              position: "top-right",
              props: {
                title: "Validation Error",
                variant: "danger",
                text: error.response.data[item],
              },
            });
          }
        } else {
          this.$toast({
            component: ToastificationContent,
            position: "top-right",
            props: {
              title: "Error",
              variant: "danger",
              text: "Failed to dispense drug",
            },
          });
        }
      }
    },
  },
};
</script>
<style scoped>
/* Modern UI Styles */
.info-card {
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  transition: all 0.3s ease;
  border: 1px solid #e9ecef;
}

.info-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
}

.border-left-primary {
  border-left: 4px solid #7367f0 !important;
}

.border-left-info {
  border-left: 4px solid #00cfe8 !important;
}

.border-left-success {
  border-left: 4px solid #28c76f !important;
}

.modern-table {
  border-radius: 8px;
  margin: 10px;
  /* overflow: hidden; */
}

.modern-table thead th {
  background: linear-gradient(135deg, #7367f0 0%, #9c88ff 100%);
  color: white;
  border: none;
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.75rem;
  letter-spacing: 0.5px;
}

.modern-table tbody tr {
  transition: all 0.2s ease;
}

.modern-table tbody tr:hover {
  background-color: #f8f9fa;
  transform: scale(1.01);
}

.shadow-sm {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1) !important;
}

/* Button Enhancements */
.btn {
  transition: all 0.3s ease;
  font-weight: 600;
}

.btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.btn-success {
  background: linear-gradient(135deg, #28c76f 0%, #48da89 100%);
  border: none;
}

.btn-primary {
  background: linear-gradient(135deg, #7367f0 0%, #9c88ff 100%);
  border: none;
}

/* Card Header Enhancements */
.card-header {
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border-bottom: 2px solid #e9ecef;
}

/* Badge Enhancements */
.badge {
  font-weight: 600;
  letter-spacing: 0.5px;
}

/* Skeleton Loading */
.b-skeleton {
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
}

@keyframes loading {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

/* Legacy Button Styles */
.button-preview {
  align-items: center;
  appearance: none;
  background-color: #fff;
  border: 1px solid #dbdbdb;
  border-radius: 0.375em;
  box-shadow: none;
  box-sizing: border-box;
  color: #363636;
  cursor: pointer;
  display: inline-flex;
  font-family: BlinkMacSystemFont, -apple-system, "Segoe UI", Roboto, Oxygen,
    Ubuntu, Cantarell, "Fira Sans", "Droid Sans", "Helvetica Neue", Helvetica,
    Arial, sans-serif;
  font-size: 1rem;
  height: 2.5em;
  justify-content: center;
  line-height: 1.5;
  padding: calc(0.5em - 1px) 1em;
  position: relative;
  text-align: center;
  user-select: none;
  -webkit-user-select: none;
  touch-action: manipulation;
  vertical-align: top;
  white-space: nowrap;
  margin-bottom: 3px;
  margin-right: 10px;
}

.button-preview:active {
  border-color: #4a4a4a;
  outline: 0;
}

.button-preview:focus {
  border-color: #485fc7;
  outline: 0;
}

.button-preview:hover {
  border-color: #b5b5b5;
  background: rgb(245, 241, 241);
}

.button-preview:focus:not(:active) {
  box-shadow: rgba(72, 95, 199, 0.25) 0 0 0 0.125em;
}

.modify {
  height: 6rem;
  font-size: 2rem;
  align-items: center;
}

.modify:hover {
  box-shadow: rgba(0, 0, 0, 0.35) 0px 5px 15px;
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
